import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "buyer" | "seller" | "provider" | "admin";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: { full_name: string; username?: string | null; phone: string | null; location: string | null; avatar_url: string | null; account_type?: "personal" | "company" | "admin"; email_verified?: boolean; admin_approved?: boolean; province?: string | null; municipality?: string | null } | null;
  refreshProfile: () => Promise<void>;
  isProfileComplete: boolean;
  roles: AppRole[];
  loading: boolean;
  rolesLoaded: boolean;
  signUp: (email: string, password: string, meta: Record<string, string | undefined>) => Promise<{ error: Error | null; userId: string | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AuthContextType["profile"]>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("full_name, username, phone, location, avatar_url, account_type, email_verified, admin_approved, province, municipality")
      .eq("id", userId)
      .single();
    if (data) setProfile(data as any);
  };

  const refreshProfile = async () => {
    if (user?.id) await fetchProfile(user.id);
  };

  const fetchRoles = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    setRoles(data ? data.map((r: any) => r.role as AppRole) : []);
    setRolesLoaded(true);
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        // Use setTimeout to avoid Supabase deadlock
        setTimeout(() => {
          fetchProfile(session.user.id);
          fetchRoles(session.user.id);
        }, 0);
      } else {
        setProfile(null);
        setRoles([]);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchRoles(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, meta: Record<string, string | undefined>) => {
    // Strip undefined values so they don't end up as the string "undefined" in raw_user_meta_data
    const data: Record<string, string> = {};
    for (const [k, v] of Object.entries(meta)) {
      if (v !== undefined && v !== null && v !== "") data[k] = v;
    }
    const { data: signUpData, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data,
        emailRedirectTo: window.location.origin,
      },
    });
    return { error: error as Error | null, userId: signUpData?.user?.id ?? null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    setRolesLoaded(false);
  };

  const hasRole = (role: AppRole) => roles.includes(role);

  const isProfileComplete = !!(
    profile &&
    profile.full_name && profile.full_name.trim() !== "" &&
    profile.phone && profile.phone.trim() !== "" &&
    profile.username && profile.username.trim() !== "" &&
    profile.province && profile.province.trim() !== "" &&
    profile.municipality && profile.municipality.trim() !== ""
  );

  return (
    <AuthContext.Provider value={{ user, session, profile, roles, loading, rolesLoaded, signUp, signIn, signOut, hasRole, refreshProfile, isProfileComplete }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
